import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { BookOpen, Microscope, BrainCircuit, Calendar as CalendarIcon, AlertCircle, Settings } from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO, isToday } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function Schedule() {
  const navigate = useNavigate();
  const [todayTopics, setTodayTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rescheduling, setRescheduling] = useState(false);

  useEffect(() => {
    fetchTodayTopics();
  }, []);

  const fetchTodayTopics = async () => {
    try {
      const response = await api.get('/topics/schedule/today');
      setTodayTopics(response.data);
    } catch (error) {
      console.error('Error fetching today topics:', error);
      toast.error('Erro ao carregar temas do dia');
    } finally {
      setLoading(false);
    }
  };

  const handleStageToggle = async (topicId, stage, currentValue) => {
    try {
      const update = { [stage]: !currentValue };
      await api.patch(`/topics/${topicId}`, update);
      
      setTodayTopics(prev => 
        prev.map(t => t.id === topicId ? { ...t, ...update } : t)
      );
      
      toast.success('Progresso atualizado!');
    } catch (error) {
      console.error('Error updating topic:', error);
      toast.error('Erro ao atualizar progresso');
    }
  };

  const handleReschedule = async () => {
    setRescheduling(true);
    try {
      const response = await api.post('/topics/reschedule');
      toast.success(response.data.message);
      fetchTodayTopics();
    } catch (error) {
      console.error('Error rescheduling:', error);
      toast.error('Erro ao reprogramar temas');
    } finally {
      setRescheduling(false);
    }
  };

  const hasPendingTopics = todayTopics.some(t => !t.reading_done || !t.deep_dive_done || !t.questions_done);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="schedule-page">
      <div className="max-w-md mx-auto px-6 py-8">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <CalendarIcon className="w-8 h-8 text-blue-600" />
              <h1 className="text-4xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Cronograma</h1>
            </div>
            <Button variant="outline" onClick={() => navigate('/schedule/manage')} className="gap-2">
              <Settings className="w-4 h-4" /> Adicionar Cronograma
            </Button>
          </div>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            {format(new Date(), "EEEE, dd 'de' MMMM", { locale: ptBR })}
          </p>
        </div>

        {hasPendingTopics && (
          <Card className="mb-6 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-900 dark:text-amber-200 mb-3">
                    Não conseguiu estudar hoje? Reprograme seus temas pendentes.
                  </p>
                  <Button 
                    onClick={handleReschedule}
                    disabled={rescheduling}
                    size="sm"
                    className="bg-amber-100 text-amber-900 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:hover:bg-amber-900/50 border border-amber-200 dark:border-amber-800"
                    data-testid="reschedule-button"
                  >
                    {rescheduling ? 'Reprogramando...' : 'Não estudei hoje'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="space-y-4">
          {todayTopics.length === 0 ? (
            <Card className="shadow-sm">
              <CardContent className="text-center py-12">
                <CalendarIcon className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">Nenhum tema programado</h3>
                <p className="text-muted-foreground mb-6">
                  Você não tem temas programados para hoje
                </p>
                <Button onClick={() => navigate('/topics/new')} data-testid="add-new-topic-button">
                  Adicionar Tema
                </Button>
              </CardContent>
            </Card>
          ) : (
            todayTopics.map((topic) => (
              <Card 
                key={topic.id} 
                className="shadow-sm hover:shadow-md transition-all cursor-pointer"
                onClick={() => navigate(`/topics/${topic.id}`)}
                data-testid={`topic-card-${topic.id}`}
              >
                <CardHeader>
                  <CardTitle className="text-xl">{topic.title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div 
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStageToggle(topic.id, 'reading_done', topic.reading_done);
                      }}
                    >
                      <Checkbox 
                        checked={topic.reading_done} 
                        data-testid={`reading-checkbox-${topic.id}`}
                      />
                      <BookOpen className="w-5 h-5 text-brand" />
                      <span className={`flex-1 ${topic.reading_done ? 'line-through text-muted-foreground' : ''}`}>
                        Leitura Rápida
                      </span>
                    </div>

                    <div 
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStageToggle(topic.id, 'deep_dive_done', topic.deep_dive_done);
                      }}
                    >
                      <Checkbox 
                        checked={topic.deep_dive_done} 
                        data-testid={`deepdive-checkbox-${topic.id}`}
                      />
                      <Microscope className="w-5 h-5 text-brand" />
                      <span className={`flex-1 ${topic.deep_dive_done ? 'line-through text-muted-foreground' : ''}`}>
                        Aprofundamento
                      </span>
                    </div>

                    <div 
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-accent transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStageToggle(topic.id, 'questions_done', topic.questions_done);
                      }}
                    >
                      <Checkbox 
                        checked={topic.questions_done} 
                        data-testid={`questions-checkbox-${topic.id}`}
                      />
                      <BrainCircuit className="w-5 h-5 text-brand" />
                      <span className={`flex-1 ${topic.questions_done ? 'line-through text-muted-foreground' : ''}`}>
                        Questões
                      </span>
                    </div>
                  </div>

                  {topic.questions_total > 0 && (
                    <div className="pt-3 border-t">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Taxa de acerto</span>
                        <span className="font-bold text-brand" data-testid={`accuracy-${topic.id}`}>
                          {topic.correct_rate}%
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
